import { API } from '../api'
import { KIND_CREDIT_SUMMARY, KIND_DUES_LOANS, KIND_POINTS_BENEFITS } from '../content'
import { isEbacThemeBrand } from '@utils/pageContentBrand'

// Inline type to avoid circular dependency with SignIn components
type SignInStepName = 'signin' | 'help-panel' | 'forgot-username-confirmation' | 'forgot-password-reset-link' | 'forgot-password-reset-form' | 'account-locked'

// Account API routes
export const API_DUES_LOANS = `${API}/${KIND_DUES_LOANS}`
export const API_CREDIT_SUMMARY = `${API}/${KIND_CREDIT_SUMMARY}`
export const API_POINTS_BENEFITS = `${API}/${KIND_POINTS_BENEFITS}`

// Account routes
export const ACCOUNT_BASE = 'account'
export const ACCOUNT_HOME_PATH = `/${ACCOUNT_BASE}/my-account`

export const ACCOUNT_POINTS_BENEFITS = 'points-benefits'
export const ACCOUNT_POINTS_BENEFITS_PATH = `/${ACCOUNT_BASE}/${ACCOUNT_POINTS_BENEFITS}`

export const ACCOUNT_BUCKET_LIST = 'bucket-list'
export const ACCOUNT_BUCKET_LIST_PATH = `/${ACCOUNT_BASE}/${ACCOUNT_BUCKET_LIST}`

export const ACCOUNT_TRIPS = 'my-trips'
export const ACCOUNT_TRIPS_PATH = `/${ACCOUNT_BASE}/${ACCOUNT_TRIPS}`
export const ACCOUNT_TRIP_DETAILS_PATH = `${ACCOUNT_TRIPS_PATH}`
export const ACCOUNT_WAITLIST = 'waitlist'
export const ACCOUNT_WAITLIST_PATH = `${ACCOUNT_TRIPS_PATH}/${ACCOUNT_WAITLIST}`
export const ACCOUNT_WAITLIST_CREATE = 'create'
export const ACCOUNT_WAITLIST_CREATE_PATH = `${ACCOUNT_WAITLIST_PATH}/${ACCOUNT_WAITLIST_CREATE}`

export const ACCOUNT_OWNERSHIP = 'my-ownership'
export const ACCOUNT_OWNERSHIP_PATH = `/${ACCOUNT_BASE}/${ACCOUNT_OWNERSHIP}`

export const ACCOUNT_CREDITS = 'credits'
export const ACCOUNT_CREDITS_PATH = `/${ACCOUNT_BASE}/${ACCOUNT_CREDITS}`

export const ACCOUNT_PROFILE = 'profile'
export const ACCOUNT_PROFILE_PATH = `/${ACCOUNT_BASE}/${ACCOUNT_PROFILE}`

export const ACCOUNT_WISH_LIST = 'wish-list'
export const ACCOUNT_WISH_LIST_PATH = `/${ACCOUNT_BASE}/${ACCOUNT_WISH_LIST}`

export const ACCOUNT_DUES_LOANS = 'dues-loans'
export const ACCOUNT_DUES_LOANS_PATH = `/${ACCOUNT_BASE}/${ACCOUNT_DUES_LOANS}`

export const SAVED_SEARCHES_PATH = `/${ACCOUNT_BASE}/saved-searches`

export const LOGIN_PATH = '/login'

// Additional account routes
export const ACCOUNT_PAYMENTS = '/account/dues-loans/payments'
export const ACCOUNT_RCI_EXCHANGES = '/account/rci-exchanges'
export const ACCOUNT_CONVERSIONS_RCI = '/account/conversions-rci'
export const ACCOUNT_RCI_DEPOSITS = '/account/deposit-points'

export function getAccountDepositsRciPath(themeBrand: string): string {
  return isEbacThemeBrand(themeBrand) ? ACCOUNT_CONVERSIONS_RCI : ACCOUNT_RCI_EXCHANGES
}

// Outside routes
export const RCI_BENEFITS = '/us/en/club-benefits/rci'
/** Public RCI landing (e.g. Explore / Join when RCI SSO is unavailable) */
export const RCI_PRE_JOIN_URL = 'https://www.rci.com/pre-rci/us/en'

// Account balance messaging constants are already defined above
// DUE_BALANCE_PRESENT, BACK_TO_MASTER, OVERDUE_PAYMENT_DESC,
// ACCOUNT_BALANCE_WARNING_PART_ONE, ACCOUNT_BALANCE_WARNING_PART_TWO

// Dashboard API
export const API_DASHBOARD = `${API}/dashboard`
export const API_UPCOMING_VACATIONS = `${API}/vacations/upcoming`

// Account labels
export const HOME = 'Home'
export const MY_ACCOUNT = 'My Account'
export const ACCOUNT_TITLE_SIR = 'Account.'
export const ACCOUNT_TITLE_EBAC = 'Account'
export const MY_TRIPS = 'My Trips'
export const TRIP_DETAILS = 'Trip Details'
export const RESERVATION_SUMMARY = 'Reservation Summary'
export const CHARGES_SUMMARY = 'Charges Summary'
export const CANCELLATION_POLICY = 'Cancellation Policy'
export const CANCELLATION_POLICY_URL = '/us/en/club-benefits/owner-guide'
export const GOOGLE_MAPS_DIR_URL = 'https://www.google.com/maps/dir/'

export const CONFIRMATION_NUMBER_LABEL = 'Confirmation Number'
export const CREDIT_CARD_LABEL = 'Credit Card'
export const CREDIT_CARD_ENDING_IN = 'Ending in'
export const TOTAL_TO_BE_REFUNDED_LABEL = 'Total to be Refunded'
export const ADD_TO_CALENDAR = 'Add to Calendar'
export const SHARE_TRIP_DETAILS = 'Share Trip Details'
export const NEW_RESORT_ALERT = 'New Resort Alert'
export const GOT_IT = 'Got It'
export const CHECK_IN = 'Check-In'
export const CHECK_OUT = 'Check-Out'
export const CHECK_IN_OUT = 'Check In/Out'
export const CHECK_IN_NO_HYPHEN = 'Check In'
export const CHECK_OUT_NO_HYPHEN = 'Check Out'
export const CHECK_IN_OUT_ARROW = 'Check In → Check Out'
export const REQUESTED_DATES = 'Requested dates'
export const SUITE_TYPE = 'Suite Type'
export const RESORT_INFORMATION = 'Resort Information'
export const GET_DIRECTIONS = 'Get Directions'
export const VIEW_RESORT_DETAILS = 'View Resort Details'
export const VIEW_RESORT_MAP = 'View Resort Map'
export const VIEW_ALL = 'View all'
export const HELPFUL_HINTS = 'Helpful Hints'
export const CANCELLATION = 'Cancellation'
export const VIEW_CANCELLATION_POLICY = 'View Cancellation Policy'
export const MODIFY_RESERVATION = 'Modify Reservation'
export const BOOK_AGAIN = 'Book Again'
export const CANCEL_RESERVATION_LABEL = 'Cancel Reservation'
export const CANCELLING_LABEL = 'Cancelling...'
export const DO_NOT_CANCEL_LABEL = "Don't Cancel Reservation"
export const RESERVATION_CANCELED_SUCCESS_MESSAGE = 'You successfully canceled your reservation.'
export const LOADING_CANCELLATION_DETAILS = 'Loading cancellation details...'
export const UNABLE_TO_RETRIEVE_CANCELLATION_INFO = 'Unable to retrieve required cancellation information. Please try again or refresh the page.'
export const MISSING_RESERVATION_TOKENS = 'Missing reservation tokens. Please wait for cancellation details to load.'
export const MISSING_RESERVATION_IDS = 'Missing reservationId. Please wait for cancellation details to load.'
export const FAILED_TO_CANCEL_RESERVATION = 'Failed to Cancel Reservation. Try again later'
export const REIMBURSEMENT_SUMMARY = 'REIMBURSEMENT SUMMARY'
export const MEMBERSHIP_REIMBURSEMENTS = 'Membership Reimbursements'
export const PAYMENT_REIMBURSEMENTS = 'Payment Reimbursements'
export const MY_RESERVATIONS = 'My reservations'
export const PAST_VACATIONS = 'Past vacations'
export const UPCOMING_VACATIONS = 'Upcoming vacations'
export const WAITLIST = 'Waitlist'
export const VIEW_ON_MAP = 'View on map'
export const VIEW_DETAILS = 'View Details'
export const MODIFY = 'Modify'
export const CANCEL = 'Cancel'
export const DECLINE = 'Decline'
export const SUBMIT = 'Submit'
export const SUBMIT_REQUEST = 'Submit Request'
export const CONFIRMATION = 'Confirmation #'
export const DATES = 'Dates'
export const RESORTS = 'Resorts'
export const LOAD_MORE = 'Load more'
export const CURRENT_WAITLISTS = 'Current waitlists'
export const OF_FOUR_REQUESTS = 'of 4 requests'
export const MAKE_A_NEW_REQUEST = 'Make a New Request'
export const MODIFY_REQUEST = 'Modify request'
export const ACTIVE_REQUEST = 'Active Request'
export const REQUEST_OFFERED = 'Request offered'
export const REQUESTED_CHECK_IN = 'Requested Check-In'
export const REQUESTED_CHECKOUT = 'Requested Checkout'
export const REQUESTED_CHECK_IN_OUT = 'Requested Check In/Out'
export const WAITLIST_ID = 'Waitlist ID'
export const WAITLIST_LEVEL = 'Waitlist Level'
export const OFFER_MADE = 'Offer Made'
export const BOOK_BY = 'Book By'
export const OFFER_CHECK_IN_OUT = 'Offer Check In/Out'
export const CANCEL_REQUEST = 'Cancel Request'
export const WAITLIST_REQUEST_STATUS_TOOLTIP =
  'Searching to fulfill your waitlist request. If/when a match has been found, you will be notified of the details and offer expiration.'
export const WAITLIST_REQUEST_LEVELS_TOOLTIP =
  'Waitlist Levels represent the likelihood that a reservation will become available, from Level 1 being highly likely to Level 3 being very unlikely.'
export const RESORT_WAITLIST_REQUEST = 'Resort Waitlist Request'
export const WAITLIST_REQUEST_FORM_TEXT = 'Please fill out the form below to be notified when your desired resort becomes available.'
export const RESORT_SELECTION_NOTE = 'Changing your resort selection will affect available suite types.'
export const MINIMUM_NIGHTS = 'Minimum Nights'
export const MINIMUM_NIGHTS_NOTE = 'Minimum nights cannot exceed desired dates.'
export const MAXIMUM_NIGHTS = 'Maximum Nights'
export const RESORT = 'Resort'
export const SUITES_SELECTED = 'Suites Selected'
export const SUITE_TYPES = 'Suite Types'
export const DETAILED_SUITE_TYPES = 'Detailed Suite Types'
export const REQUESTED_SUITE_TYPES = 'Requested Suite Types'
export const CELL = 'Cell'
export const CELL_NOTE = 'USA cell numbers only.'
export const WAITLIST_CONTACT_INFO_MESSAGE = "A waitlist request doesn't guarantee a reservation. Request must adhere to Club Guidelines."
export const WAITLIST_GUIDELINES_NOTE = "A waitlist request doesn't guarantee a reservation. Requests must adhere to The Club Guidelines. Please review the waitlist rules"
export const WAITLIST_MATCHES_FOUND_DESCRIPTION =
  'Your requested dates and resort are available to book. Instead of joining the waitlist, search availibility to book your vacation. You can also continue your waitlist request without the matched suites.'
export const SUITES = 'Suites'
export const CONTINUE_WAITLIST_REQUEST = 'Continue Waitlist Request'
export const SEARCH_AVAILABILITY = 'Search Availability'
export const RESORT_NAME = 'Resort Name'
export const MIN_MAX_NIGHTS = 'Min/Max Nights'
export const EMAIL_ADDRESS = 'Email Address'
export const STATUS = 'Status'
export const CLOSE = 'Close'
export const WAITLIST_REQUEST = 'Waitlist Request'
export const DUES_LOANS = 'Dues & loans'
export const QUESTIONS_CONCERNS = 'Questions or concerns? Contact us through our customer support page.'
export const DUES_SUMMARY = 'Dues summary'
export const LOANS_SUMMARY = 'Loans summary'
export const MAKE_A_PAYMENT = 'Make a payment'
export const PAYMENTUS_URL = 'https://ipn.paymentus.com/cp/wdhm'
export const DUES = 'Dues'
export const LOANS = 'Loans'
export const PAYMENTS = 'Payments'
export const NEXT_DUE_DATE = 'Next due date'
export const PAST_DUE = 'Past due'
export const FEES_INTEREST = 'Fees/Interest'
export const AMOUNT_DUE = 'Amount due'
export const PAST_DUE_BALANCE_MESSAGE =
  'Your account has a past due balance. Reservations are not allowed until the balance is paid. For dues or loan payments, please select make a payment. To pay reservation fees, please contact the Vacation Planning Center at'
export const PRINCIPAL_BALANCE = 'Principal balance'
export const PAID_IN_FULL = 'Paid in full'
export const SPECIAL_ASSESSMENT = 'Special assessment'
export const SPECIAL_ASSESSMENTS = 'Special assessments'
export const PAID_AMOUNT = 'Paid amount'
export const DUE_DATE = 'Due date'
export const DEPOSITS_RCI = 'RCI Exchange.'
export const UPCOMING_TRIP_INTRO_TEXT = 'An instant classic'
export const UPCOMING_TRIP = 'Upcoming trip'
export const STARTS_IN = 'Starts in'
export const DAYS = 'days'
export const VIEW_TRIP_DETAILS = 'View trip details'
export const EXPLORE_DESTINATION_GUIDE = 'Explore destination guide'
export const LEARN_ABOUT_TRAVEL_INSURANCE = 'Learn about travel insurance'
export const SEE_MORE_UPCOMING_TRIPS = 'See more upcoming trips'
export const OUTSTANDING_FEES_MESSAGE = 'To deposit or exchange your points, your account must be free of outstanding balances and fees.'
export const MODIFY_OUTSTANDING_FEES_MESSAGE = 'To modify a reservation, your account must be free of outstanding balances and fees.'
export const PAY_BILL = 'Pay bill'
export const HOME_HERO_HEADING = 'Spirit'
export const TRIPS = 'Trips'
export const UPCOMING_TRIPS = 'Upcoming trips'
export const UPCOMING_TRIPS_DESCRIPTION = 'This is where adventure takes shape.'
/** Default chunk size for paginated upcoming trips (per ticket). */
export const UPCOMING_TRIPS_PAGE_SIZE = 10
/** Default number of page links shown by MkPagination on mobile viewports. Feature-neutral. */
export const PAGINATION_MOBILE_LINK_COUNT = 3
/** Default number of page links shown by MkPagination on desktop viewports. Feature-neutral. */
export const PAGINATION_DESKTOP_LINK_COUNT = 7
export const TRAVELER_NAME = 'Traveler name'
export const IN = 'In'
export const NO_UPCOMING_TRIPS_TITLE = 'No trips planned yet.'
export const NO_UPCOMING_TRIPS_SUBTITLE = 'Start exploring and map out what’s next.'
export const PAST_TRIPS = 'Past trips'
export const PAST_TRIPS_DESCRIPTION = 'A glimpse of your travel history'
/** My Trips page — Sports Illustrated only; EBAC and other brands use the shared strings above. */
export const UPCOMING_TRIPS_DESCRIPTION_SIR = 'Your next getaway is on deck'
export const NO_UPCOMING_TRIPS_TITLE_SIR = 'You don’t have any trips lined up.'
export const NO_UPCOMING_TRIPS_SUBTITLE_SIR = 'Start planning your next big play.'
export const PAST_TRIPS_DESCRIPTION_SIR = 'A highlight reel of your travel history.'
export const CALENDAR_YEAR = 'Calendar year'
export const UPCOMING_PAST_TRIPS_LABEL = 'Upcoming & Past Trips'
export const WAITLIST_REQUESTS_TITLE = 'Waitlist requests'
export const WAITLIST_REQUESTS_SUBTITLE_OF = '{count} of {max} requested'
export const WAITLIST_MAX_REQUESTS_REACHED_MESSAGE = "You've reached the maximum number of waitlist requests. To make a new request, cancel or decline an existing one."
export const NO_WAITLIST_REQUESTS_TITLE = "You don't have any waitlist requests"
export const NO_WAITLIST_REQUESTS_SUBTITLE =
  'If your desired resort or dates aren’t available, join the Waitlist! As soon as your resort and dates open up, Club Wyndham will notify you, and you’ll be one step closer to that dream vacation.'
export const CREATE_WAITLIST_REQUEST_TITLE = 'Create a new request'
export const CREATE_WAITLIST_REQUEST_SUBTITLE =
  'If your desired resort or dates aren’t available, join the Waitlist! As soon as your resort and dates open up, Club Wyndham will notify you, and you’ll be one step closer to that dream vacation.'
export const LOADING_WAITLIST = 'Loading your waitlist requests'
export const LOADING_WAITLIST_SUBTITLE = 'Please wait while we fetch your current waitlist requests'
export const LOADING_CREATE_WAITLIST_REQUEST_TITLE = 'Creating your waitlist request'
export const LOADING_CREATE_WAITLIST_SUBTITLE = 'Please wait while we create your waitlist request'
export const RESERVATION_CANCELLED = 'Reservation cancelled'
export const DATE_CANCELLED_LABEL = 'Date cancelled'
export const CANCELLED_BY_LABEL = 'Cancelled by'

export const WAITLIST_CREATE_LEAVE_TITLE = 'Are you sure you want to leave?'
export const WAITLIST_CREATE_LEAVE_TEXT = 'Exiting this page will cancel your waitlist request creation. You will need to start over to create a new request.'
export const WAITLIST_CREATE_LEAVE_DESCRIPTION = 'Exiting this page will erase your selection and you will have to begin your waitlist request process all over again.'
export const WAITLIST_CREATE_LEAVE_CONFIRM_CTA = 'Yes, Exit'
export const WAITLIST_CREATE_LEAVE_PRIMARY_BUTTON = 'Yes, exit request'
export const WAITLIST_CREATE_LEAVE_SECONDARY_BUTTON = 'No, return to request'
export const WAITLIST_CREATE_LEAVE_CANCEL_CTA = 'No, Stay'
export const WAITLIST_CREATE_ERROR_MESSAGE = 'Failed to create waitlist request. Please try again.'

export const EXPLORE_ALL_RESORTS = 'Explore all resorts'
export const LOADING_UPCOMING_TITLE = 'Loading your trips'
export const LOADING_UPCOMING_SUBTITLE = 'Please wait while we fetch your upcoming vacations'
export const CONFIRMATION_WORD_NUMBER = 'Confirmation number'

export const RCI_DESCRIPTION =
  'Take your credits beyond your WorldMark ownership. Explore exchange options with RCI and give the gift of vacation through the Charitable Giving Program.'
export const LEARN_MORE = 'Learn more'
export const EXCHANGE_OPTIONS = 'Exchange options'
export const EMAIL_INVALID_MESSAGE = 'Please enter valid email address'

// Account labels for dues, loans, and balances
export const DUE_BALANCE_PRESENT = 'In case of due balance present'
export const BACK_TO_MASTER = 'Back to master'
export const OVERDUE_PAYMENT_DESC = 'You have an overdue payment.'
export const ACCOUNT_BALANCE_WARNING_PART_ONE = 'You cannot book a vacation until your account balance is settled. Please call'
export const ACCOUNT_BALANCE_WARNING_PART_TWO = 'for assistance or make a payment online.'

// Export payment-related constants
export * from './payment'

// Credits and Benefits
export const AVAILABLE_POINTS = 'Available Points'
export const BENEFIT_DETAILS = 'Benefit Details'
export const BENEFIT_HISTORY = 'Benefit History'
export const BENEFITS_SUMMARY = 'Benefits Summary'
export const BROWSE_BY = 'Browse by'
export const CURRENT_USE_YEAR = 'Current Use Year'
export const USE_YEARS = 'Use Years'
// export const MAKE_A_PAYMENT = 'Make a Payment' // duplicate, consider a shared terms file/section/library?
export const MEMBER = 'Member'
export const MEMBER_ID = 'Member ID'
export const MVP = 'MVP'
export const MVP_ID = 'MVP ID'
export const OWNER_EDUCATION = 'Owner Education'
export const PTS_AVAILABLE = 'Pts Available'
export const TRANSACTION_HISTORY = 'Transaction History'
export const USING_POINTS_LIKE_A_PRO = 'Using Points Like a Pro'
export const WELCOME = 'Welcome'
export const EBAC_PENDING_STATUS = 'PENDING'
export const EBAC_PENDING_MESSAGE = 'Check back in a day or two to see if your status has been updated.'

export const INVALID_PHONE_FORMAT_MESSAGE = 'Please enter a valid phone number'

// SignIn component constants and labels (grouped by step)
export const SIGNIN_LABELS = {
  // ============================================================================
  // SHARED / COMMON LABELS (used across multiple steps)
  // ============================================================================
  CONTINUE: 'Continue',
  LOADING: 'Loading...',
  BACK_TO_SIGN_IN: 'Back to Sign In',
  ERROR_OCCURRED: 'An error occurred. Please try again.',
  STEP_NOT_FOUND: 'Step not found',

  // ============================================================================
  // SIGN IN STEP 1 (SignInStep1.tsx)
  // ============================================================================
  USERNAME: 'Username',
  PASSWORD: 'Password',
  REMEMBER_ME: 'Remember me',
  SIGN_IN: 'Join the Action',
  SIGN_IN_TITLE: 'GET IN THE GAME',
  SIGN_IN_SUBMIT_BUTTON_TITLE: 'SIGN IN',
  NEED_HELP_SIGNING_IN: 'Need help signing in?',
  REGISTER: 'Register',
  SIGNIN_DESCRIPTION: 'Sign in to your account to access your reservations and preferences.',
  SIGN_IN_FALLBACK: 'Sign In',
  WELCOME_FALLBACK: 'Welcome',
  LOGIN_ERROR: 'Login Error',
  SIGNIN_FAILED: 'SignIn failed:',

  // ============================================================================
  // HELP PANEL STEP (HelpPanelStep.tsx)
  // ============================================================================
  HELP_RECOVERY_TITLE: 'LOGIN HELP',
  HELP_DESCRIPTION: 'Having trouble signing in? We can help you recover your account.',
  FORGOT_USERNAME_TAB: 'Forgot Username',
  FORGOT_PASSWORD_TAB: 'Forgot Password',

  // --- ForgotUsernameTab.tsx ---
  FORGOT_USERNAME: 'Forgot Username',
  RECOVER_USERNAME: 'Recover Your Username',
  RECOVER_USERNAME_DESCRIPTION: 'To recover your username, please confirm your identity using the information you provided during registration.',
  RECOVER_USERNAME_BUTTON: 'Recover Username',
  OWNER_NUMBER: 'Owner Number (11 digits)',
  MEMBER_NUMBER: 'Member number (11 digits)',
  COMPANY_NAME_LABEL: 'Company Name',
  COMPANY_OR_TRUST_NAME_LABEL: 'Company or Trust Name',
  FIRST_NAME_LABEL: 'First Name',
  LAST_NAME_LABEL: 'Last Name',
  SUFFIX_OPTIONAL: 'Suffix',
  PHONE_NUMBER: 'Phone Number',
  PHONE_COUNTRY_LABEL: 'Phone Country',
  ENTER_CONTRACT_DETAILS: 'Enter details as listed on contract',
  SELECT_VERIFICATION_METHOD: 'Select a verification method',
  ADD_YOUR_ACCOUNT_DETAILS: 'Add your account details',
  CONTRACT_DETAILS_LEGEND: 'Contract Details',
  CONTRACT_NUMBER_LABEL: 'Contract Number',
  DEFAULT_PHONE_COUNTRY: 'USA',
  VERIFICATION_METHOD: 'Verification Method',
  EMAIL_LABEL: 'Email',
  PHONE_LABEL: 'Phone',
  REGISTER_AS_COMPANY: 'Register as Company',
  OWNER_NUMBER_REQUIRED: 'Owner number is required and must be 11 digits',
  FIRST_NAME_REQUIRED: 'This field is required',
  LAST_NAME_REQUIRED: 'This field is required',
  COMPANY_NAME_REQUIRED: 'This field is required',
  PHONE_NUMBER_REQUIRED: 'Phone number is required and invalid',
  PHONE_REQUIRED: INVALID_PHONE_FORMAT_MESSAGE,
  CONTRACT_NUMBER_REQUIRED: 'Contract number is required and invalid',

  // --- ForgotPasswordTab.tsx ---
  FORGOT_PASSWORD: 'Forgot Password',
  RESET_PASSWORD: 'Reset Your Password',
  RESET_PASSWORD_DESCRIPTION: "Don't worry! Enter your email address and we'll send you a secure link to reset your password.",
  FORGOT_PASSWORD_DEFAULT_SUBTITLE: 'If an account is associated with the provided information, you will receive an email. Follow the link in the email to reset your password.',
  FORGOT_PASSWORD_DEFAULT_DESCRIPTION: 'To reset your password, please confirm your identity using the email or username provided during registration.',
  EMAIL_ADDRESS: 'Email Address',
  ENTER_EMAIL_PLACEHOLDER: 'Enter your email address',
  SEND_EMAIL_BUTTON: 'Send Email',
  SENDING: 'Sending...',
  EMAIL_SENT: 'Email Sent',
  SEND_RESET_LINK: 'Send Reset Link',
  EMAIL_NOT_FOUND: 'No account found with this email address.',
  TOO_MANY_ATTEMPTS: 'Too many password reset attempts. Please try again later.',

  // ============================================================================
  // USERNAME RECOVERED STEP (UsernameRecoveredStep.tsx)
  // ============================================================================
  USERNAME_RECOVERED: 'Your Username',
  USERNAME_RECOVERED_DESCRIPTION: 'Welcome back! Here is your username for your account:',
  SIGN_IN_WITH_USERNAME: 'Sign in with username',
  YOUR_USERNAME: 'YOUR USERNAME',
  WELCOME_BACK_USERNAME: 'Welcome back! Here is your username for your account:',
  WELCOME_BACK_USERNAME_OPTION2: "Welcome back. Here's the username for your account:",

  // ============================================================================
  // PASSWORD RECOVERY EMAIL SENT STEP (PasswordRecoveryEmailSentStep.tsx)
  // ============================================================================
  CHECK_YOUR_EMAIL: 'Check Your Email',
  CHECK_EMAIL_DESCRIPTION: "We've sent a password reset email to your email address. Please check your inbox and follow the instructions.",
  CHECK_EMAIL_ALT: 'Check your email',
  EMAIL_CHECKED: "I've checked my email",
  BACK_TO_RECOVERY_OPTIONS: 'Back to recovery options',
  RESEND_WARNING: 'Give us a moment! Request a new email with a reset link in 3 minutes.',
  RESET_LINK_WARNING: 'Give us a moment! Request a new email with a reset link in 3 minutes.',
  RESET_PASSWORD_WARNING: 'Give us a moment! Request a new email with a reset link in 3 minutes.',
  DIDNT_GET_EMAIL: "Didn't get an email?",
  RESEND_EMAIL: 'Resend email',
  CALL_US_PHONE: 'Call us at 800-457-0103',
  CALL_US_PHONE_LINK: 'tel:800-457-0103',
  NO_USERNAME_FOR_RESEND: 'No username available for resend',
  PASSWORD_RECOVERY_DESCRIPTION: 'If an account is associated with the provided information, you will receive an email. Follow the link in the email to reset your password.',
  RECOVERY_EMAIL_SENT: 'Recovery Email Sent',

  // ============================================================================
  // RESET PASSWORD STEP (ResetPasswordStep.tsx)
  // ============================================================================
  SET_NEW_PASSWORD: 'Set New Password',
  SIGN_IN_RESET_PASSWORD: 'RESET PASSWORD',
  SET_NEW_PASSWORD_DESCRIPTION: 'Please enter your new password below. Make sure it meets all the requirements listed.',
  SET_PASSWORD_DESCRIPTION: 'Please enter your new password below.',
  SET_PASSWORD_BUTTON: 'Set New Password',
  RESET_NEW_PASSWORD_BUTTON: 'RESET PASSWORD AND LOG IN',
  SET_NEW_PASSWORD_BUTTON: 'Set New Password',
  NEW_PASSWORD_LABEL: 'New Password',
  CONFIRM_PASSWORD: 'Confirm Password',
  CONFIRM_PASSWORD_LABEL: 'Confirm Password',
  ENTER_NEW_PASSWORD_PLACEHOLDER: 'Enter your new password',
  ENTER_NEW_PASSWORD: 'Enter your new password',
  CONFIRM_NEW_PASSWORD: 'Confirm your new password',
  HIDE_PASSWORD: 'Hide password',
  SHOW_PASSWORD: 'Show password',
  PASSWORD_COMPLEXITY_ERROR: 'Your password must meet the complexity requirements. Please choose a different password that is not your current password.',
  CURRENT_PASSWORD_ERROR: 'You cannot use your current password. Please choose a different password.',
  ADDITIONAL_REQUIREMENTS_LABEL: 'Additional requirements:',

  // ============================================================================
  // ACCOUNT LOCKED STEP (Shared AccountLocked component)
  // ============================================================================
  ACCOUNT_LOCKED_CAPTION: 'START EXPLORING IN 15 MINUTES',
  ACCOUNT_LOCKED_TITLE: 'ACCOUNT TEMPORARILY LOCKED',
  ACCOUNT_TEMPORARILY_LOCKED: 'Account Temporarily Locked',
  ACCOUNT_LOCKED_DESCRIPTION:
    "We know you're excited to discover your next adventure, but after 3 failed verification attempts, we've temporarily locked the account for security reasons. Please try again in 15 minutes.",
  BACK_TO_SIGN_IN_BUTTON: 'Back to sign in',
  BACK_TO_SIGN_IN_WITH_TIME: 'Back to sign in ({time})',
  TIME_REMAINING: 'Time remaining:',
  START_EXPLORING: 'START EXPLORING IN 15 MINUTES',
  ACCOUNT_LOCKED: 'This account has been locked. Please contact support.',

  // ============================================================================
  // LEGACY / DEPRECATED (from older step registry - may need cleanup)
  // ============================================================================
  TEMPORARY_CODE: 'Temporary Code',
  SECURITY_QUESTION_1: 'Security Question 1',
  SECURITY_QUESTION_2: 'Security Question 2',
  ANSWER_1: 'Answer 1',
  ANSWER_2: 'Answer 2',
  ENTER_CODE_SENT_TO_EMAIL: 'Enter the code sent to your email',
  ENTER_SECURITY_QUESTION: 'Enter your security question',
  ENTER_ANSWER: 'Enter your answer',
  RESET_PASSWORD_SUBTITLE: 'If an account is associated with the provided information, you will receive an email. Follow the link in the email to reset your password.',
} as const

// Validation error messages
export const VALIDATION_MESSAGES = {
  OWNER_NUMBER_REQUIRED: 'Owner number is required and must be 11 digits',
  FIRST_NAME_REQUIRED: 'This field is required',
  LAST_NAME_REQUIRED: 'This field is required',
  COMPANY_NAME_REQUIRED: 'This field is required',
  VERIFICATION_METHOD_REQUIRED: 'Please select a verification method',
  PHONE_REQUIRED: 'Phone number is required and invalid',
  CONTRACT_NUMBER_REQUIRED: 'Contract number is required and invalid',
  EMAIL_INVALID: 'Please enter a valid email address',
  USERNAME_INVALID: 'Username must be at least 6 characters and start with a letter',
  PASSWORD_INVALID: 'Password must be at least 8 characters and include uppercase, lowercase, and a number',
  EMAIL_REQUIRED: 'Email is required. Please enter your email address.',
  USERNAME_REQUIRED: 'Username is required. Please enter a username.',
  PASSWORD_REQUIRED: 'Password is required. Please enter a password.',
  VERIFICATION_CODE_REQUIRED: 'Verification code is required. Please enter the code sent to your device.',
  POSTAL_CODE_REQUIRED: 'Please enter a valid zip code',
  PASSWORDS_DO_NOT_MATCH: 'Passwords do not match',
  NO_AUTH_TOKEN: 'No authentication token available. Please check your email verification link or restart registration.',
  // MFA error messages
  MFA_NO_STATE_TOKEN_RESEND: 'This code has expired - please restart the login process to proceed',
  MFA_NO_FACTOR_ID: 'No factor ID or resend link available for resending code',
  MFA_RESEND_FAILED: 'Failed to resend code',
} as const

export const FORM_FIELD_TYPES = {
  TEXT: 'text',
  EMAIL: 'email',
  PASSWORD: 'password',
  TEL: 'tel',
} as const

export const FORM_FIELD_NAMES = {
  VERIFICATION_CODE: 'verificationCode',
  STATE_TOKEN: 'stateToken',
  EMAIL: 'email',
  USERNAME: 'username',
  PASSWORD: 'password',
} as const

export const INPUT_STATUS = {
  ERROR: 'error',
  NONE: 'none',
  SUCCESS: 'success',
} as const

export const FORM_AUTOCOMPLETE = {
  OFF: 'off',
  ON: 'on',
  NEW_PASSWORD: 'new-password',
  CURRENT_PASSWORD: 'current-password',
  USERNAME: 'username',
  EMAIL: 'email',
} as const

export const CSS_CLASSES = {
  TEXT_ERROR: 'text-error',
  TEXT_SUCCESS: 'text-success',
} as const

export const TRIPS_TABS = {
  MY_TRIPS: 'my-trips',
  WAITLIST: 'waitlist',
} as const

// Error codes for recovery operations
export const RECOVERY_ERROR_CODES = {
  // Okta error codes
  OKTA_TOO_MANY_REQUESTS: 'E0000118',
  OKTA_INVALID_TOKEN: 'E0000011',
  OKTA_EXPIRED_TOKEN: 'E0000105',
  OKTA_USER_NOT_FOUND: 'E0000007',
  OKTA_INVALID_PASSCODE: 'E0000068',
  OKTA_ACCOUNT_LOCKED: 'E0000014',
  OKTA_USER_LOCKED: 'E0000069',
  OKTA_AUTH_STATE_ERROR: 'E0000079',
  // Business system codes
  GPMBV002_NO_PARTY_FOUND: 'GPMBV002',
  GPMBV003_INACTIVE_PARTY: 'GPMBV003',
  // Application error codes
  ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
  TOO_MANY_ATTEMPTS: 'TOO_MANY_ATTEMPTS',
  EMAIL_NOT_FOUND: 'EMAIL_NOT_FOUND',
} as const

// Okta response status codes
export const OKTA_STATUS = {
  SUCCESS: 'SUCCESS',
  MFA_ENROLL: 'MFA_ENROLL',
  MFA_ENROLL_ACTIVATE: 'MFA_ENROLL_ACTIVATE',
  MFA_CHALLENGE: 'MFA_CHALLENGE',
  MFA_REQUIRED: 'MFA_REQUIRED',
  LOCKED_OUT: 'LOCKED_OUT',
  PASSWORD_EXPIRED: 'PASSWORD_EXPIRED',
  PENDING: 'pending',
} as const

// Error summary text fragments for error detection
export const ERROR_SUMMARY_FRAGMENTS = {
  INVALID_PASSCODE: 'Invalid Passcode',
  INVALID_TOKEN: 'Invalid token',
  ACCOUNT_LOCKED: 'Account locked',
  USER_LOCKED: 'User Locked',
} as const

// HTTP headers and content types
export const HTTP_HEADERS = {
  ACCEPT: 'accept',
  CONTENT_TYPE: 'Content-Type',
  BRAND: 'brand',
} as const

export const CONTENT_TYPES = {
  JSON: 'application/json',
} as const

export const OKTA_DOMAIN = 'okta.wyndhamdestinations.com'
export const OKTA_WELCOME_PATH = '/welcome/'

export const OKTA_API_CONFIG = {
  OKTA_AUTHN_FACTORS_PATH: '/api/v1/authn/factors',
  VERIFY_RESEND_PATH: '/verify/resend',
} as const

export const AUTH_HEADERS = {
  AUTHORIZATION_PREFIX: 'SSWS ',
  USER_AGENT: 'Mozilla/5.0 (compatible; MakoApp/1.0)',
  X_FORWARDED_FOR_LOCALHOST: '127.0.0.1',
  X_OKTA_USER_AGENT: 'okta-auth-js/7.10.1',
} as const

// Storage keys for registration and authentication flows
export const REGISTRATION_STORAGE_KEYS = {
  STATE_TOKEN: 'stateToken',
  MFA_NEXT_LINK: 'mfaNextLink',
  MFA_SESSION_TOKEN: 'mfaSessionToken',
  MFA_USER: 'mfaUser',
} as const

// Session type identifiers
export const SESSION_TYPES = {
  AUTHENTICATED: 'authenticated',
  UNAUTHENTICATED: 'unauthenticated',
} as const

// Placeholder and sentinel values
export const PLACEHOLDER_VALUES = {
  FACTOR_ID: 'placeholder-factor-id',
  USER_DETAILS: 'USER_DETAILS',
  UNKNOWN_USER: 'unknown',
} as const

// Time constants (in milliseconds unless otherwise noted)
export const TIME_CONSTANTS = {
  // Lockout durations
  LOCKOUT_DURATION_MS: 15 * 60 * 1000, // 15 minutes in milliseconds
  // Redirect delays
  REDIRECT_DELAY_MS: 1500, // 1.5 seconds
  // Error auto-clear timeouts
  ERROR_AUTO_CLEAR_MS: 15000, // 15 seconds
  // Snackbar durations (in seconds)
  SNACKBAR_SHORT_DURATION: 5, // 5 seconds
  SNACKBAR_LONG_DURATION: 8, // 8 seconds
} as const

// Navigation paths
export const NAVIGATION_PATHS = {
  HOME: '/',
  SIGN_IN: '/sign-in',
  REGISTER: '/register',
} as const

// Validation thresholds
export const VALIDATION_THRESHOLDS = {
  // Show warning after this many failed attempts
  MFA_WARNING_THRESHOLD: 2,
  // Maximum MFA attempts before lockout
  MFA_LOCKOUT_MAX_ATTEMPTS: 5,
} as const

// API response field names
export const API_RESPONSE_FIELDS = {
  SESSION_TOKEN: 'sessionToken',
  STATE_TOKEN: 'stateToken',
  STATUS: 'status',
  FACTOR_ID: 'factorId',
  REGISTRATION_TOKEN: 'registrationToken',
  PARTY: 'party',
  EMBEDDED: '_embedded',
  LINKS: '_links',
  FACTORS: 'factors',
  FACTOR: 'factor',
  USER: 'user',
  VERIFY: 'verify',
  NEXT: 'next',
  HREF: 'href',
  PROFILE: 'profile',
  LOGIN: 'login',
} as const

// Session field names for ProtectedDataStore
export const SESSION_FIELDS = {
  SESSION_TYPE: 'sessionType',
  AUTH_BRAND: 'authBrand',
  AUTHENTICATED_USER: 'authenticatedUser',
  CODE_VERIFIER: 'codeVerifier',
  SESSION_TOKEN: 'sessionToken',
  USERNAME: 'username',
  RECOVERY_TOKEN: 'recoveryToken',
} as const

// Field names used in error detection
export const ERROR_CHECK_FIELDS = {
  EMAIL: 'EMAIL',
  USERNAME: 'USERNAME',
  USER_NAME: 'USER_NAME',
} as const

// Help text and tooltips
export const HELP_TEXT = {
  OWNER_NUMBER_TOOLTIP: 'Your owner number can be found on your contract. Please enter the 9-11 digit number without hyphens.',
} as const

export const SIGNIN_PLACEHOLDERS = {
  USERNAME: '',
  PASSWORD: '',
  EMAIL: 'Enter your email address',
  OWNER_NUMBER: 'Enter your owner number',
  MEMBER_NUMBER: 'Enter 11-digit member number',
  COMPANY_NAME: 'Enter your company name',
  COMPANY_OR_TRUST_NAME: 'Enter company or trust name',
  FIRST_NAME: 'Enter your first name',
  LAST_NAME: 'Enter your last name',
  PHONE: 'Enter your phone number',
  CONTRACT_NUMBER: 'Enter your contract number',
  NEW_PASSWORD: 'Enter your new password',
  CONFIRM_PASSWORD: 'Confirm your new password',
} as const

export const SIGNIN_AUTOCOMPLETE = {
  USERNAME: 'username',
  PASSWORD: 'current-password',
  EMAIL: 'email',
  GIVEN_NAME: 'given-name',
  FAMILY_NAME: 'family-name',
  ORGANIZATION: 'organization',
  TEL: 'tel',
  NEW_PASSWORD: 'new-password',
} as const

// SignIn step keys - used for step navigation
// Type ensures all values are SignInStepName
const signInStepKeysBase = {
  SIGNIN: 'signin',
  HELP_PANEL: 'help-panel',
  FORGOT_USERNAME_CONFIRMATION: 'forgot-username-confirmation',
  FORGOT_PASSWORD_RESET_LINK: 'forgot-password-reset-link',
  FORGOT_PASSWORD_RESET_FORM: 'forgot-password-reset-form',
  ACCOUNT_LOCKED: 'account-locked',
} as const satisfies Record<string, SignInStepName>

export const SIGNIN_STEP_KEYS: {
  readonly [K in keyof typeof signInStepKeysBase]: SignInStepName
} = signInStepKeysBase

// SignIn API operation names
export const SIGNIN_API_OPERATIONS = {
  FORGOT_USERNAME: 'forgotUsername',
  FORGOT_PASSWORD: 'forgotPassword',
  EMAIL_VERIFICATION: 'emailVerification',
  TEMP_CODE_VERIFICATION: 'tempCodeVerification',
  SECURITY_QUESTIONS: 'securityQuestions',
  ACCOUNT_SEARCH: 'accountSearch',
  PASSWORD_RESET: 'passwordReset',
} as const

// Register component constants and labels (grouped by step)
export const REGISTER_LABELS = {
  // ============================================================================
  // SHARED / COMMON LABELS (used across multiple steps)
  // ============================================================================
  CONTINUE: 'CONTINUE',
  LOADING: 'Loading...',
  BACK: 'Back',
  STEP_X_OF_Y: 'Step {current} of {total}',
  FIELD_REQUIRED: 'This field is required',

  // ============================================================================
  // ACCOUNT LOOKUP STEP (MkRegisterAccountLookup.tsx)
  // ============================================================================
  ACCOUNT_LOOKUP_CAPTION: "IT'S TIME TO KICK OFF",
  ACCOUNT_LOOKUP_TITLE: 'Register',
  ACCOUNT_LOOKUP_DESCRIPTION: 'Please provide your contract information to verify your account',
  SELECT_VERIFICATION_METHOD: 'Select a Verification Method',
  FIRST_NAME: 'First Name',
  LAST_NAME: 'Last Name',
  COMPANY_NAME: 'Company Name',
  COMPANY_OR_TRUST_NAME: 'Company or Trust Name',
  PHONE: 'Phone',
  PHONE_NUMBER: 'Phone Number',
  CONTRACT_NUMBER: 'Contract Number',
  CONTRACT: 'Contract',
  OWNER_NUMBER: 'Member Number (11 digits)',
  POSTAL_CODE: 'Postal Code',
  VERIFICATION_METHOD: 'Verification Method',
  PHONE_COUNTRY: 'Phone Country',
  REGISTER_AS_INDIVIDUAL: 'Register as an individual?',
  REGISTER_AS_COMPANY_OR_TRUST: 'Register as a company or trust?',
  SUFFIX_OPTIONAL: 'Suffix',

  // ============================================================================
  // CHECK EMAIL STEP (MkRegisterCheckEmail.tsx)
  // ============================================================================
  // (No specific constants needed - uses shared labels)

  // ============================================================================
  // USER DETAILS STEP (MkRegisterSubmitUserDetails.tsx)
  // ============================================================================
  USER_DETAILS_TITLE: 'Add your account details.',
  USER_DETAILS_CAPTION: 'WELCOME BACK',
  USER_DETAILS_DESCRIPTION: 'Set up your username and email for your new account',
  EMAIL: 'EMAIL ADDRESS',
  USERNAME: 'USERNAME',
  INVALID_USERNAME: 'Invalid username',
  INVALID_EMAIL: 'Invalid email',
  USERNAME_TAKEN_ERROR: 'Username is already taken',
  SUGGESTED_USERNAME_TEXT: 'Suggested username:',
  USERNAME_CRITERIA_1: 'Username must be at least 6 characters long',
  USERNAME_CRITERIA_2: 'Username must start with a letter',
  VERIFY_AND_CONTINUE: 'Verify and Continue',

  // ============================================================================
  // SET PASSWORD STEP (RegisterSetPassword.tsx)
  // ============================================================================
  SET_PASSWORD_CAPTION: 'WELCOME BACK',
  SET_PASSWORD_TITLE: 'Set your password',
  SET_PASSWORD_DESCRIPTION: 'Create a strong password to secure your account',
  PASSWORD: 'Password',
  CONFIRM_PASSWORD: 'Confirm Password',
  NEW_PASSWORD: 'New password',
  CONFIRM_PASSWORD_LABEL: 'Confirm password',
  ENTER_NEW_PASSWORD: 'Enter your new password',
  SET_PASSWORD: 'Set password',
  // ============================================================================
  // MFA SETUP SELECTOR STEP (MkRegisterMfaSetupSelector.tsx)
  // ============================================================================
  SET_UP_MFA: 'Choose your multi-factor authentication method',
  NICE_TO_SEE_YOU_AGAIN: 'Nice to see you again',
  WELCOME_BACK_EYE_BROW: 'WELCOME BACK',
  CHOOSE_MFA_DESCRIPTION: 'Choose how you want to receive verification codes to secure your account.',
  MFA_METHOD: 'MFA Method',
  MFA_METHOD_LABEL: 'MFA Method',
  SELECT_MFA_METHOD: 'Select MFA method',
  SMS_TEXT_MESSAGE: 'SMS (Text Message)',
  AUTHENTICATOR_APP: 'Authenticator App',
  TEXT_MESSAGE_SMS: 'Text message (SMS)',
  SEND_CODE: 'Send code',
  SENDING_CODE: 'Sending code...',

  // ============================================================================
  // MFA SETUP DETAILS STEP (MkRegisterMfaSetupDetails.tsx)
  // ============================================================================
  MFA_SETUP_DETAILS_TITLE: 'ADDITIONAL DETAILS',
  VERIFY_PHONE: 'Verify Your Phone Number',
  VERIFY_PHONE_DESCRIPTION: 'We will send a verification code to your phone number to complete MFA setup.',
  PHONE_NUMBER_LABEL: 'Phone Number',
  COUNTRY_LABEL: 'Country',
  WELL_SEND_CODE: "We'll send a verification code to this number",
  ENTER_PHONE_NUMBER: 'Enter your phone number',
  SEND_VERIFICATION_CODE: 'Send Verification Code',
  MFA_SETUP_EMAIL_DESCRIPTION: 'Provide an email address for {factorType} authentication. You will receive a code via {factorType} at the provided email address.',
  MFA_SETUP_PHONE_DESCRIPTION: 'Provide a phone number for {factorType} authentication. You will receive a code via {factorType} at the provided phone number.',
  MFA_EMAIL_ADDRESS_LABEL: 'Email Address',
  MFA_EMAIL_PLACEHOLDER: 'Enter your email address',
  SETTING_UP: 'Setting up...',

  // Countries
  UNITED_STATES: 'United States',
  CANADA: 'Canada',
  MEXICO: 'Mexico',
  UNITED_KINGDOM: 'United Kingdom',

  // ============================================================================
  // MFA VERIFY CODE STEP (MkRegisterMfaVerifyCode.tsx)
  // ============================================================================
  MFA_VERIFY_CODE_TITLE: 'Enter verification code',
  MFA_VERIFY_CODE_CAPTION: "You're Almost There",
  MFA_VERIFY_CODE_STEP_TITLE: 'Verify your identity',
  MFA_VERIFY_CODE_DESCRIPTION: 'Enter the code we sent to verify your identity',
  MFA_CODE_SENT_MESSAGE: 'We sent your code to verify your identity',
  MFA_AUTHORIZATION_CODE_LABEL: 'Authentication Code',
  /** Primary CTA on MFA verify step (Figma: Continue). */
  MFA_VERIFY_CONTINUE: 'CONTINUE',
  MFA_RESEND_CODE_BUTTON: 'RESEND CODE',
  MFA_RESENDING_BUTTON: 'RESENDING...',
  MFA_CODE_RESENT_SUCCESS: 'Code resent successfully!',
  MFA_CODE_SENT_EMAIL_TEMPLATE: 'We sent your code to {email}',
  MFA_CODE_SENT_SMS_TEMPLATE: 'We sent your code to {phone}',
  MFA_CODE_SENT_CALL_TEMPLATE: 'We called you at {phone}',
  VERIFY: 'Verify',
  CONFIRM: 'CONFIRM',
  ARIA_RESEND_VERIFICATION_CODE: 'Resend verification code',

  // ============================================================================
  // ACCOUNT LOCK SCREEN STEP (Shared AccountLocked component)
  // ============================================================================
  ACCOUNT_LOCK_TITLE: 'YOUR ACCOUNT HAS BEEN LOCKED',
  ACCOUNT_LOCK_CAPTION: 'UNABLE TO VERIFY',
  ACCOUNT_LOCK_STEP_TITLE: 'YOUR ACCOUNT HAS BEEN LOCKED',
  ACCOUNT_LOCK_DESCRIPTION: 'Your account has been locked due to multiple failed attempts',
  ACCOUNT_LOCK_MFA_WARNING: 'Multiple failed MFA verification attempts were detected from your account.',
  ACCOUNT_LOCK_LOGIN_WARNING: 'Multiple failed login attempts were detected from your account.',
  ACCOUNT_LOCK_MFA_DESCRIPTION: 'After 5 failed tries we’ve locked your account for security. Call us and we’ll unlock it so you can get back to planning your next vacation.',
  ACCOUNT_LOCK_LOGIN_DESCRIPTION:
    'For your security, your account has been temporarily locked after five failed verification attempts. Please contact 866-272-1286 if you need help unlocking your account.',
  CALL_US_BUTTON: 'CALL US FOR ASSISTANCE',

  // ============================================================================
  // ACTIVATION ERROR STEP (MkRegisterActivationError.tsx)
  // ============================================================================
  ACTIVATION_ERROR_TITLE: 'Activation Error',
  ACTIVATION_ERROR_CAPTION: 'Activation Failed',
  ACTIVATION_ERROR_STEP_TITLE: 'Unable to activate account',
  ACTIVATION_ERROR_DESCRIPTION: 'There was an error processing your activation link',
  TRY_AGAIN: 'Try Again',
  ERROR_OCCURRED_FALLBACK: 'An error occurred',

  // ============================================================================
  // COMPLETION STEP
  // ============================================================================
  MFA_CONTINUE_TO_SIGNIN: 'CONTINUE TO SIGN IN',

  // ============================================================================
  // MFA ERROR MESSAGES
  // ============================================================================
  MFA_NO_STATE_TOKEN_ERROR: 'No valid state token available for MFA factor creation. The authentication flow may need to be restarted.',

  // ============================================================================
  // SUCCESS MESSAGES
  // ============================================================================
  MFA_VERIFICATION_SUCCESS: 'You have been successfully logged in.',
  PASSWORD_SET_SUCCESS: "Password set successfully! Now let's set up multi-factor authentication.",
  REGISTRATION_COMPLETE: 'Registration completed successfully! Welcome to the Club!',
  REGISTRATION_COMPLETE_REDIRECT: 'Registration completed. You will be redirected to the home page.',

  // ============================================================================
  // ERROR MESSAGES
  // ============================================================================
  INVALID_CODE_MESSAGE: "You've entered an invalid code.",
  /** Shown when Okta/proxy error indicates the MFA OTP/passcode timed out (see registrationMfaVerifyErrors.isOktaMfaPasscodeExpiredError). */
  MFA_VERIFICATION_CODE_EXPIRED: 'This verification code has expired. Choose Resend to get a new code, then enter the latest code.',
  /** Shown on the attempt before lockout (e.g. 4th failed verify when max is 5). CMS may still key this as `mfaOneMoreAttempt`. */
  MFA_BEFORE_ACCOUNT_LOCKOUT_WARNING: "You've entered an invalid code. After 5 failed attempts, your account will be locked.",
  MFA_SETUP_ERROR: 'MFA setup error. Please try again.',
  AUTHENTICATION_INCOMPLETE: 'Authentication incomplete. Please try again.',
  ACCOUNT_ISSUE_CONTACT_SUPPORT: 'Account issue detected. Please contact support.',
  MFA_VERIFICATION_FAILED: 'MFA verification failed. Too many attempts will lock your account.',
  REGISTRATION_FAILED: 'Registration failed. Please try again or contact support.',
  /** Used when verify/submit HTTP error response cannot be parsed as expected JSON. */
  REGISTRATION_VERIFY_HTTP_BODY_INVALID: 'Failed to submit registration data. Please try again.',
  AUTH_ERROR_REFRESH: 'Authentication error. Please refresh the page and try again.',
  SESSION_EXPIRED: 'Your verification session has expired. Please start the sign-in process again.',
  INVALID_FACTOR_ID: 'MFA setup failed. Please try again.',
} as const

// MFA UI configuration
export const MFA_UI_CONFIG = {
  SUCCESS_MESSAGE_TIMEOUT_MS: 3000,
  /** Short local parts (2–4 chars): show first character + mask the rest. */
  EMAIL_LOCAL_SHORT_MAX_LEN: 4,
  /** Longer local parts: leading asterisks + this many trailing characters visible (aligns with phone-style tail reveal). */
  EMAIL_MASK_LEADING_ASTERISKS: 4,
  EMAIL_MASK_TAIL_VISIBLE: 4,
  PHONE_LAST_DIGITS_COUNT: 3,
  PHONE_MASK_MIN_LENGTH: 4,
} as const

// Account Lockout Screen Constants
export const ACCOUNT_LOCKOUT = {
  // Icons
  ICON_NAME: 'lock-keyhole',
  ICON_SET: 'fal',

  // Password Lockout (5 attempts) - Temporary lockout with 15-minute timer
  PASSWORD_CAPTION: 'TOO MANY FAILED ATTEMPTS',
  PASSWORD_TITLE: 'Account locked',
  PASSWORD_BODY:
    'For your security, your account has been temporarily locked after five failed verification attempts. Please contact 866-272-1286 if you need help unlocking your account.',
  PASSWORD_MAX_ATTEMPTS: 3,

  // MFA Critical Lockout - Failed MFA after correct password, requires phone unlock
  MFA_CRITICAL_CAPTION: 'UNABLE TO VERIFY',
  MFA_CRITICAL_TITLE: 'YOUR ACCOUNT HAS BEEN LOCKED',
  MFA_CRITICAL_BODY:
    'For your security, your account has been temporarily locked after five failed verification attempts. Please contact 866-272-1286 if you need help unlocking your account.',
  MFA_CRITICAL_BUTTON: REGISTER_LABELS.CALL_US_BUTTON,

  // Actions - Reusing existing constants
  CALL_US_BUTTON: REGISTER_LABELS.CALL_US_BUTTON,
  BACK_TO_SIGNIN_BUTTON: SIGNIN_LABELS.BACK_TO_SIGN_IN,
} as const

export const REGISTER_PLACEHOLDERS = {
  FIRST_NAME: '',
  LAST_NAME: '',
  EMAIL: '',
  USERNAME: '',
  PASSWORD: '',
  CONFIRM_PASSWORD: '',
  PHONE: '',
  CONTRACT_NUMBER: '',
  OWNER_NUMBER: '',
  COMPANY_NAME: '',
  POSTAL_CODE: '',
} as const

export const REGISTER_AUTOCOMPLETE = {
  FIRST_NAME: 'given-name',
  LAST_NAME: 'family-name',
  EMAIL: 'email',
  USERNAME: 'username',
  PASSWORD: 'new-password',
  CONFIRM_PASSWORD: 'new-password',
  PHONE: 'tel',
  CONTRACT_NUMBER: 'off',
  OWNER_NUMBER: 'off',
  COMPANY_NAME: 'organization',
  POSTAL_CODE: 'postal-code',
} as const

// Point of Contact section constants
export const CONTRACTS_POINT_OF_CONTACT_OWNER_LABEL = 'Owner'
export const CONTRACTS_POINT_OF_CONTACT_ADDRESS_LABEL = 'Address'
export const CONTRACTS_POINT_OF_CONTACT_EMAIL_LABEL = 'Email'
export const CONTRACTS_POINT_OF_CONTACT_PHONE_LABEL = 'Phone number'
export const CONTRACTS_POINT_OF_CONTACT_TITLE = 'Point of Contact'
export const CONTRACTS_POINT_OF_CONTACT_DESCRIPTION = 'To make any changes to your point of contact please call'
export const CONTRACTS_POINT_OF_CONTACT_PHONE_NUMBER = '866-272-1286'

// Account > Saved Searches
export const EXPLORE_RESORTS = 'Explore Resorts'
export const FAILED_TO_DELETE_SAVED_SEARCH = 'Failed to delete saved search!'
export const FAILED_TO_LOAD_SAVED_SEARCHES = 'An error occurred. Please reload the page, try again later, or call \n[Number] for assistance.'
export const LOADING_SAVED_SEARCHES = 'Loading saved searches...'
export const MAX_SEARCHES_PARAGRAPH = 'You can have up to 10 Saved Searches.'
export const SEARCH_OUR_RESORTS_TO_GET_INSPIRED = 'Scout your perfect resort and gear up for your next getaway.'
export const START_A_NEW_SEARCH = 'Start a New Search'
export const UNDO = 'Undo'
export const YOU_HAVE_NO_SAVED_SEARCHES = 'You haven’t saved any plays yet.'
export const YOUR_SAVED_SEARCH_WAS_DELETED = 'Your Saved Search has been deleted!'
export const DATES_FOR_SEARCH_HAVE_PASSED = 'The saved dates have passed for this search. Please provide new travel dates.'
export const SIR_SUPPORT_PHONE_NUMBER = '866-272-1286'
export const EBAC_SUPPORT_PHONE_NUMBER = '866-271-6192'

export const TRIPS_PAGE_SIZE = 10
export const TRIPS_FIRST_PAGE = 0

export const FAILED_TO_FETCH_TRIPS_EBAC_MESSAGE = `Something got lost on the trail. Try refreshing the page or check back shortly. If the issue sticks around, call ${EBAC_SUPPORT_PHONE_NUMBER} for support.`
export const FAILED_TO_FETCH_TRIPS_SIR_MESSAGE = `Flag on the play. Reload the page, try again soon, or call ${SIR_SUPPORT_PHONE_NUMBER} for backup.`

export const PROFILE_TOOLTIP_TEXT = (brand: string) => {
  const mainText = 'Does something not look right? To change your name or for additional help, please call'
  if (brand === 'sir') {
    return `${mainText} ${SIR_SUPPORT_PHONE_NUMBER}`
  }
  return `${mainText} ${EBAC_SUPPORT_PHONE_NUMBER}`
}

export const PROFILE_TOOLTIP_BUTTON_LABEL = 'Show profile help information'

export const CALL_ACTION = 'Call'
export const CLOSE_ACTION = 'Close'

// Charges Summary constants
export const CHARGES_SUMMARY_TOTAL_LABEL = 'Total'
export const CHARGES_SUMMARY_ITEM_FALLBACK = 'item'
export const CHARGES_SUMMARY_ITEMIZED_ID_PREFIX = 'charges-summary-itemized-'
export const CHARGES_SUMMARY_CHARGE_GROUP_KEY_PREFIX = 'charge-group-'

// Charge unit codes — aligned with backend MkConstants currency codes
export const CHARGE_UNIT_USD = 'USD'
